import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faCheck,
  faCamera,
  faImages,
  faAngleRight,
} from '@fortawesome/free-solid-svg-icons';

const IMAGE_BASE = process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES;

const ProjectQuoteModal = ({ 
  visible, 
  onClose, 
  projects = [], 
  selectedCategory,
  brands = [],
  onQuoteSubmit,
  isLoading = false
}) => {
  // States
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);

  // console.log(projects);
  // console.log(selectedProject);

  // In ProjectQuoteModal.js, update the useEffect:
  useEffect(() => {
    if (projects.length > 0 && !selectedProject) {
      setSelectedProject(projects[0]);
    }
  }, [projects, selectedProject]); // Add selectedProject to dependencies

  // Add this additional safety check
  useEffect(() => {
    if (visible && projects.length > 0 && !selectedProject) {
      setSelectedProject(projects[0]);
    }
  }, [visible, projects, selectedProject]);

  // Reset states when modal closes
  useEffect(() => {
    if (!visible) {
      setCurrentStep(1);
      setSelectedProject(projects.length > 0 ? projects[0] : null);
      setSelectedBrands([]);
      setUploadedImages([]);
    }
  }, [visible, projects]);

  // Handle brand selection/deselection
  const handleBrandSelect = (brand) => {
    setSelectedBrands(prev => {
      const isSelected = prev.find(b => b._id === brand._id);
      if (isSelected) {
        return prev.filter(b => b._id !== brand._id);
      } else {
        return [...prev, brand];
      }
    });
  };

  // Handle image upload from gallery
  const handleImageUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = 'image/*';
    input.onchange = (e) => {
      if (e.target.files && e.target.files.length > 0) {
        const files = Array.from(e.target.files);
        const newImages = files.map(file => ({
          url: URL.createObjectURL(file),
          fileName: file.name || `image_${Date.now()}.jpg`,
          type: file.type || 'image/jpeg',
        }));
        setUploadedImages(prev => [...prev, ...newImages]);
        // Auto proceed to submit step after upload
        setCurrentStep(3);
      }
    };
    input.click();
  };

  // Handle camera capture (uses file input with capture attribute for web/mobile compatibility)
  const handleCameraCapture = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment'; // Attempts to use camera on supported devices
    input.onchange = (e) => {
      if (e.target.files && e.target.files.length > 0) {
        const file = e.target.files[0];
        const newImage = {
          url: URL.createObjectURL(file),
          fileName: file.name || `camera_${Date.now()}.jpg`,
          type: file.type || 'image/jpeg',
        };
        setUploadedImages(prev => [...prev, newImage]);
        // Auto proceed to submit step after capture
        setCurrentStep(3);
      }
    };
    input.click();
  };

  const removeImage = (index) => {
    setUploadedImages(prev => {
      const imgToRemove = prev[index];
      if (imgToRemove && imgToRemove.url) {
        URL.revokeObjectURL(imgToRemove.url);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  // Handle "Add More Photos" - navigate back to upload section
  const handleAddMorePhotos = () => {
    setCurrentStep(2); // Navigate back to upload step
  };

  const handleSubmit = () => {
    if (selectedBrands.length === 0) {
      alert('Error', 'Please select at least one brand');
      return;
    }

    if (uploadedImages.length === 0) {
      alert('Error', 'Please upload at least one image');
      return;
    }

    const quoteData = {
      project: selectedProject,
      category: selectedCategory,
      brands: selectedBrands,
      images: uploadedImages.map(img => ({
        uri: img.url, // Use 'url' for web blob URLs, but map to 'uri' for consistency
        fileName: img.fileName,
        type: img.type,
      })),
      timestamp: new Date().toISOString(),
    };

    onQuoteSubmit?.(quoteData);
    
    alert('Success', 'Quote request submitted successfully!');
    onClose();
    // Reset form
    setCurrentStep(1);
    setSelectedBrands([]);
    setUploadedImages([]);
  };

  // STEP 1: Project, Category & Brands Selection
  const renderSelectionStep = () => (
    <div>
      {/* Category Display */}
      <div className="mb-6">
        <p className="text-sm text-gray-600 mb-2">Category</p>
        {selectedCategory && (
          <div className="flex flex-row items-center p-2 bg-gray-50 rounded-lg border border-gray-300">
            <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center mr-3">
              <p className="text-gray-700 font-bold text-base">
                {selectedCategory.product_category_name?.charAt(0) || 'C'}
              </p>
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {selectedCategory.product_category_name}
              </p>
            </div>
            <FontAwesomeIcon icon={faCheck} size={14} className="text-green-500" />
          </div>
        )}
      </div>
      {/* Project Display */}
      <div className="mb-6">
        <p className="text-sm text-gray-600 mb-2">Project</p>
        <div className="overflow-x-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <div className="flex flex-row" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {projects
              .filter(project => project) // Remove any null projects
              .reduce((rows, project, index) => {
                if (index % 2 === 0) rows.push([project]);
                else rows[rows.length - 1].push(project);
                return rows;
              }, [])
              .map((column, colIndex) => (
                <div key={colIndex} className="mr-3 flex flex-col">
                  {column.map((project) => {
                    const isSelected = selectedProject?._id === project._id;
                    return (
                      <button
                        key={project._id}
                        onClick={() => setSelectedProject(project)}
                        className={`flex flex-row items-center p-2 bg-gray-50 rounded-lg border mb-2 ${isSelected ? 'border-gray-900' : 'border-gray-300'}`}
                        type="button"
                      >
                        <div className="w-7 h-7 bg-gray-200 rounded-lg flex items-center justify-center mr-2">
                          <span className="text-gray-700 font-bold text-lg">🏗️</span>
                        </div>
                        <div className="flex-1" style={{ maxWidth: '100px' }}>
                          <p className="text-sm font-semibold text-gray-900" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {project.projectName || project.name || 'Unnamed Project'}
                          </p>
                          {(project.project_location || project.location) && (
                            <p className="text-xs text-gray-600" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {project.project_location || project.location}
                            </p>
                          )}
                        </div>
                        {isSelected && <FontAwesomeIcon icon={faCheck} size={12} className="text-green-500 ml-1" />}
                      </button>
                    );
                  })}
                </div>
              ))
            }
          </div>
        </div>
      </div>

      {/* Brands Selection */}
      <div className="mb-6">
        <div className="flex flex-row justify-between items-center mb-2">
          <p className="text-sm text-gray-600">Select Brands</p>
          <p className="text-xs text-gray-500">
            {selectedBrands.length} selected
          </p>
        </div>

     <div className="overflow-x-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
  {/* Changed flex-row-reverse → flex-row so items start from left */}
  <div className="flex flex-row">
    {brands.reduce((rows, brand, index) => {
      if (index % 3 === 0) rows.push([brand]);
      else rows[rows.length - 1].push(brand);
      return rows;
    }, []).map((row, rowIndex) => (
      <div key={rowIndex} className="flex flex-row mr-3">
        {row.map((item) => {
          const isSelected = selectedBrands.find(b => b._id === item._id);
          return (
            <button
              key={item._id}
              onClick={() => handleBrandSelect(item)}
              className="flex flex-col items-center p-2 w-24"
              type="button"
            >
              <div className="relative">
                <div
                  className={`w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center border-2 ${
                    isSelected ? 'border-gray-900' : 'border-gray-200'
                  }`}
                >
                  {item.image ? (
                    <img
                      src={`${IMAGE_BASE}${item.image}`}
                      className="w-16 h-16 object-contain"
                      alt={item.name}
                    />
                  ) : (
                    <p className="text-gray-700 font-bold text-lg">
                      {item.name?.charAt(0) || 'B'}
                    </p>
                  )}
                </div>
                {isSelected && (
                  <div className="absolute top-1 right-1 bg-gray-900 rounded-full w-5 h-5 flex items-center justify-center">
                    <FontAwesomeIcon icon={faCheck} size={10} className="text-white" />
                  </div>
                )}
              </div>

              {/* Brand name below image */}
              <p
                className="mt-2 text-xs text-center font-medium text-gray-600"
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {item.name}
              </p>
            </button>
          );
        })}
      </div>
    ))}
  </div>
</div>


      </div>

      {/* Continue Button */}
      <button
        onClick={() => setCurrentStep(2)}
        disabled={selectedBrands.length === 0}
        className={`w-full py-3 rounded-lg ${selectedBrands.length === 0 ? 'bg-gray-200' : 'bg-gray-900'}`}
        type="button"
      >
        <p className={`text-center font-medium text-base ${selectedBrands.length === 0 ? 'text-gray-500' : 'text-white'}`}>
          Continue 
        </p>
      </button>
    </div>
  );

  // STEP 2: Photo Upload
  const renderUploadStep = () => (
    <div>
      <p className="text-sm text-gray-600 mb-2 text-center">
        Add reference photo(s)
      </p>
      <p className="text-xs text-gray-500 mb-6 text-center">
        {selectedCategory?.product_category_name}
      </p>

      {/* Large Upload Options */}
      <div className="mb-6">
        <button
          onClick={handleCameraCapture}
          className="flex flex-row items-center p-5 rounded-xl border border-gray-200 mb-3 w-full"
          type="button"
        >
          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mr-4">
            <FontAwesomeIcon icon={faCamera} size={24} className="text-gray-600 text-xl" />
          </div>
          <div className="flex-1 justify-start">
            <p className="text-sm font-medium text-gray-900 text-start">Take Photo</p>
            <p className="text-xs text-gray-500 text-start">Use your camera to capture images</p>
          </div>
          <FontAwesomeIcon icon={faAngleRight} size={16} className="text-gray-400" />
        </button>

        <button
          onClick={handleImageUpload}
          className="flex flex-row items-center p-5 rounded-xl border border-gray-200 w-full"
          type="button"
        >
          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mr-4">
            <FontAwesomeIcon icon={faImages} size={24} className="text-gray-600 text-xl" />
          </div>
          <div className="flex-1 justify-start">
            <p className="text-sm font-medium text-gray-900 text-start">Choose from Gallery</p>
            <p className="text-xs text-gray-500 text-start">Select existing photos from your device</p>
          </div>
          <FontAwesomeIcon icon={faAngleRight} size={16} className="text-gray-400" />
        </button>
      </div>

      {/* Back Button */}
      <button
        onClick={() => setCurrentStep(1)}
        className="w-full py-3 rounded-lg border border-gray-300 mt-2"
        type="button"
      >
        <p className="text-center font-medium text-gray-700">Back</p>
      </button>
    </div>
  );

  // STEP 3: Review & Submit
  const renderSubmitStep = () => (
    <div>
      <p className="text-sm text-gray-600 mb-2">
        Review your quote request before submitting
      </p>
      <p className="text-xs text-gray-500 mb-6">
        {selectedCategory?.product_category_name} • {uploadedImages.length} photo(s)
      </p>

      {/* Project & Category Summary */}
      <div className="mb-6">
        <p className="text-sm font-medium text-gray-900 mb-3">Project</p>
        <div className="bg-gray-50 p-2 rounded-lg">
          <p className="font-semibold text-gray-900">
            {selectedProject?.projectName || selectedProject?.name || 'No project selected'}
          </p>
        </div>
      </div>

      {/* Brands Summary */}
      <div className="mb-6">
        <p className="text-sm font-medium text-gray-900 mb-3">Selected Brands ({selectedBrands.length})</p>
        <div className="bg-gray-50 p-4 rounded-lg">
          <p className="text-gray-600 text-sm">
            {selectedBrands.map(b => b.name).join(', ')}
          </p>
        </div>
      </div>

      {/* Uploaded Images */}
      <div className="mb-6">
        <p className="text-sm font-medium text-gray-900 mb-3">Photos</p>
        <div className="overflow-x-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <div className="flex flex-row gap-3">
            {uploadedImages.map((img, idx) => (
              <div key={idx} className="relative">
                <img
                  src={img.url}
                  className="w-20 h-20 rounded-lg bg-gray-100 object-cover"
                  alt={`Uploaded ${idx + 1}`}
                />
                <button
                  onClick={() => removeImage(idx)}
                  className="absolute -top-2 -right-2 bg-gray-800 rounded-full w-5 h-5 flex items-center justify-center"
                  type="button"
                >
                  <FontAwesomeIcon icon={faTimes} size={12} className="text-white" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-row gap-3">
        <button
          onClick={handleAddMorePhotos} // Updated to navigate back to upload section
          className="flex-1 py-3 rounded-lg border border-gray-300"
          type="button"
        >
          <p className="text-center font-medium text-gray-700">Add More Photos</p>
        </button>
        
        <button
          onClick={handleSubmit}
          className="flex-1 py-3 rounded-lg bg-gray-900"
          type="button"
        >
          <p className="text-center font-medium text-white">
            Request for Quote
          </p>
        </button>
      </div>
    </div>
  );

   if (!visible) return null;

  return (
  <div className="fixed inset-0 bg-black bg-opacity-40 flex items-end z-50">
    <div className="bg-white rounded-t-3xl max-h-[90vh] overflow-hidden w-full">
      
      {/* Header with Progress */}
      <div className="px-5 pt-5 pb-2 border-b border-gray-100">
        <div className="flex flex-row justify-between items-center mb-2">
          <p className="text-lg font-semibold text-gray-900">
            Project Quote
          </p>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded"
            type="button"
          >
            <FontAwesomeIcon icon={faTimes} size={20} className="text-gray-400" />
          </button>
        </div>
        
        {/* Progress Steps */}
        <div className="flex flex-row items-center justify-center">
          <div className="flex flex-row items-center">
            {/* Step 1 - Selection */}
            <div className="flex flex-row items-center">
              <div
                className={`w-6 h-6 rounded-full border flex justify-center items-center ${
                  currentStep > 1
                    ? "border-green-500 bg-green-500"
                    : currentStep === 1
                    ? "border-gray-800 bg-gray-800"
                    : "border-gray-300 bg-white"
                }`}
              >
                {currentStep > 1 ? (
                  <FontAwesomeIcon icon={faCheck} size={12} className="text-white" />
                ) : (
                  <span
                    className={`text-xs ${
                      currentStep >= 1 ? "text-white" : "text-gray-400"
                    }`}
                  >
                    1
                  </span>
                )}
              </div>
              <span className="text-xs text-gray-600 ml-2 mr-4">Selection</span>
            </div>

            {/* Connector */}
            <div className="w-8 h-px bg-gray-300 mx-2" />

            {/* Step 2 - Upload */}
            <div className="flex flex-row items-center">
              <div
                className={`w-6 h-6 rounded-full border flex justify-center items-center ${
                  currentStep > 2
                    ? "border-green-500 bg-green-500"
                    : currentStep === 2
                    ? "border-gray-800 bg-gray-800"
                    : "border-gray-300 bg-gray-100"
                }`}
              >
                {currentStep > 2 ? (
                  <FontAwesomeIcon icon={faCheck} size={12} className="text-white" />
                ) : (
                  <span
                    className={`text-xs ${
                      currentStep >= 2 ? "text-white" : "text-gray-400"
                    }`}
                  >
                    2
                  </span>
                )}
              </div>
              <span className="text-xs text-gray-600 ml-2 mr-4">Upload</span>
            </div>

            {/* Connector */}
            <div className="w-8 h-px bg-gray-300 mx-2" />

            {/* Step 3 - Submit */}
            <div className="flex flex-row items-center">
              <div
                className={`w-6 h-6 rounded-full border flex justify-center items-center ${
                  currentStep >= 3
                    ? "border-gray-800 bg-gray-800"
                    : "border-gray-300 bg-gray-100"
                }`}
              >
                <span
                  className={`text-xs ${
                    currentStep >= 3 ? "text-white" : "text-gray-400"
                  }`}
                >
                  3
                </span>
              </div>
              <span className="text-xs text-gray-600 ml-2">Submit</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable content (fills remaining space, no bottom gap) */}
      <div
        className="overflow-y-auto px-5 py-4"
        style={{ maxHeight: "calc(90vh - 100px)" }}
      >
        {currentStep === 1 && renderSelectionStep()}
        {currentStep === 2 && renderUploadStep()}
        {currentStep === 3 && renderSubmitStep()}
      </div>
    </div>
  </div>
);

};

export default ProjectQuoteModal;
